import { useEffect, useRef, useState } from "react";
import { api, eventQuery } from "../api";
import type { Session } from "../types";
import { ActionIcon, IconActionButton, IconActionLink } from "./AdminActions";

type JitsiStatus = { state: "connecting" | "waiting" | "live" | "stopped" | "error"; roomUrl: string; message: string; receivedBytes: number; audioDetected: boolean; lastAudioAt: string | null };
type CaptureStatus = {
  jitsi?: JitsiStatus | null;
  jitsiBaseUrl?: string;
  videoMeetingUrl?: string;
  status: "idle" | "live" | "processing" | "ready" | "error";
  sourceTranscript: string;
  summary: string;
  recordingAvailable: boolean;
  recordingUrl: string;
  errorMessage: string;
  languages: string[];
  ai: { available: boolean; reason: string };
  testAudio: null | { name: string; size: number; url: string };
};

function websocketUrl(path: string) {
  return `${location.protocol === "https:" ? "wss:" : "ws:"}//${location.host}${path}`;
}

function pcm16(values: Float32Array) {
  const result = new Int16Array(values.length);
  for (let index = 0; index < values.length; index += 1) result[index] = Math.max(-32768, Math.min(32767, Math.round(Math.max(-1, Math.min(1, values[index])) * 32767)));
  return result;
}

function resample(input: Float32Array, inputRate: number, outputRate = 24_000) {
  if (inputRate === outputRate) return input;
  const ratio = inputRate / outputRate;
  const output = new Float32Array(Math.max(1, Math.floor(input.length / ratio)));
  for (let index = 0; index < output.length; index += 1) {
    const start = Math.floor(index * ratio), end = Math.min(input.length, Math.floor((index + 1) * ratio));
    let sum = 0; for (let cursor = start; cursor < Math.max(start + 1, end); cursor += 1) sum += input[Math.min(cursor, input.length - 1)];
    output[index] = sum / Math.max(1, end - start);
  }
  return output;
}

export function LiveTranslationCapture({ session }: { session: Session }) {
  const [data, setData] = useState<CaptureStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [testCountdown, setTestCountdown] = useState(0);
  const [localError, setLocalError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedAudioDeviceId, setSelectedAudioDeviceId] = useState("");
  const [detectingDevices, setDetectingDevices] = useState(false);
  const [localTestAudio, setLocalTestAudio] = useState<File | null>(null);
  const [jitsiUrl, setJitsiUrl] = useState(session.videoMeetingUrl || (session.streamUrl?.includes("meet.") ? session.streamUrl : ""));
  const [jitsiPassword, setJitsiPassword] = useState("");
  const socketRef = useRef<WebSocket | null>(null);
  const stopMediaRef = useRef<() => void>(() => undefined);

  async function refresh() {
    try {
      const result = await api<CaptureStatus>(`/api/sessions/${session.id}/live-translation?${eventQuery(session.eventId)}`);
      setData(result); if (result.videoMeetingUrl) setJitsiUrl(result.videoMeetingUrl); setLoadError("");
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "No se pudo consultar la traducción simultánea");
    }
  }

  useEffect(() => {
    refresh().catch(() => undefined);
    navigator.mediaDevices?.enumerateDevices().then((devices) => setAudioDevices(devices.filter((device) => device.kind === "audioinput"))).catch(() => undefined);
    return () => { stopMediaRef.current(); socketRef.current?.close(); };
  }, [session.id]);
  useEffect(() => {
    if (!data || (!["live", "processing"].includes(data.status) && !["connecting", "waiting", "live"].includes(data.jitsi?.state || ""))) return;
    const timer = window.setInterval(() => refresh().catch(() => undefined), 3000);
    return () => window.clearInterval(timer);
  }, [data?.status, data?.jitsi?.state, session.id]);

  function connect() {
    return new Promise<WebSocket>((resolve, reject) => {
      const socket = new WebSocket(websocketUrl(`/api/live/sessions/${session.id}/capture`));
      socket.binaryType = "arraybuffer";
      socket.onopen = () => { socketRef.current = socket; resolve(socket); };
      socket.onerror = () => reject(new Error("No se pudo abrir el canal de captura. Verificá que el proxy admita WebSocket."));
      socket.onclose = (event) => { if (event.code >= 4400 && event.reason) setLocalError(event.reason); refresh().catch(() => undefined); };
      socket.onmessage = (event) => { try { const message = JSON.parse(String(event.data)); if (message.type === "capture.started") setData((current) => current ? { ...current, status: "live" } : current); } catch { /* audio no llega al capturador */ } };
    });
  }

  async function detectAudioInputs() {
    setDetectingDevices(true); setLocalError("");
    let permissionStream: MediaStream | null = null;
    try {
      if (!navigator.mediaDevices?.getUserMedia || !navigator.mediaDevices?.enumerateDevices) throw new Error("Este navegador no permite seleccionar dispositivos de audio.");
      permissionStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const devices = (await navigator.mediaDevices.enumerateDevices()).filter((device) => device.kind === "audioinput");
      setAudioDevices(devices);
      if (selectedAudioDeviceId && !devices.some((device) => device.deviceId === selectedAudioDeviceId)) setSelectedAudioDeviceId("");
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : "No se pudieron detectar las entradas de audio");
    } finally {
      permissionStream?.getTracks().forEach((track) => track.stop());
      setDetectingDevices(false);
    }
  }

  async function startMicrophone() {
    setBusy(true); setLocalError("");
    let stream: MediaStream | null = null;
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error("Este navegador no permite capturar el micrófono. Usá HTTPS o localhost.");
      stream = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: selectedAudioDeviceId ? { exact: selectedAudioDeviceId } : undefined, channelCount: 1, echoCancellation: false, noiseSuppression: false, autoGainControl: false } });
      const activeStream = stream;
      const socket = await connect();
      const context = new AudioContext();
      const source = context.createMediaStreamSource(activeStream);
      const processor = context.createScriptProcessor(4096, 1, 1);
      const silent = context.createGain(); silent.gain.value = 0;
      processor.onaudioprocess = (event) => {
        if (socket.readyState !== WebSocket.OPEN) return;
        const chunk = pcm16(resample(event.inputBuffer.getChannelData(0), context.sampleRate));
        socket.send(chunk.buffer);
      };
      source.connect(processor); processor.connect(silent); silent.connect(context.destination);
      stopMediaRef.current = () => { processor.disconnect(); source.disconnect(); silent.disconnect(); activeStream.getTracks().forEach((track) => track.stop()); context.close().catch(() => undefined); };
      setData((current) => current ? { ...current, status: "live" } : current);
    } catch (error) { stream?.getTracks().forEach((track) => track.stop()); socketRef.current?.close(); setLocalError(error instanceof Error ? error.message : "No se pudo iniciar la captura"); }
    finally { setBusy(false); }
  }

  async function startTestAudio() {
    setBusy(true); setLocalError("");
    try {
      if (!localTestAudio && !data?.testAudio) throw new Error("Seleccioná un archivo de audio para realizar la prueba.");
      let audioBytes: ArrayBuffer;
      if (localTestAudio) audioBytes = await localTestAudio.arrayBuffer();
      else {
        const response = await fetch(data!.testAudio!.url, { credentials: "same-origin" });
        if (!response.ok) throw new Error("No se pudo leer el audio de prueba guardado en el evento");
        audioBytes = await response.arrayBuffer();
      }
      const context = new AudioContext();
      const decoded = await context.decodeAudioData(audioBytes);
      const mono = decoded.getChannelData(0);
      const samples = pcm16(resample(mono, decoded.sampleRate));
      const socket = await connect();
      let cancelled = false;
      let timer = 0;
      stopMediaRef.current = () => { cancelled = true; window.clearInterval(timer); context.close().catch(() => undefined); };
      setData((current) => current ? { ...current, status: "live" } : current);
      // Da tiempo para abrir la sesión en Mobile, activar el audio y colocarse
      // los auriculares. La captura ya figura en vivo durante esta cuenta regresiva.
      for (let remaining = 8; remaining > 0 && !cancelled; remaining -= 1) {
        setTestCountdown(remaining);
        await new Promise((resolve) => window.setTimeout(resolve, 1000));
      }
      setTestCountdown(0);
      if (cancelled || socket.readyState !== WebSocket.OPEN) return;
      let offset = 0;
      const samplesPerChunk = 2400;
      timer = window.setInterval(() => {
        if (socket.readyState !== WebSocket.OPEN || offset >= samples.length) {
          window.clearInterval(timer);
          if (offset >= samples.length && socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: "stop" }));
          context.close().catch(() => undefined); return;
        }
        const end = Math.min(samples.length, offset + samplesPerChunk);
        socket.send(samples.slice(offset, end).buffer); offset = end;
      }, 100);
    } catch (error) { socketRef.current?.close(); setLocalError(error instanceof Error ? error.message : "No se pudo reproducir el audio de prueba"); }
    finally { setTestCountdown(0); setBusy(false); }
  }

  async function startJitsi() {
    setBusy(true); setLocalError("");
    try {
      const result = await api<{ jitsi: JitsiStatus }>(`/api/sessions/${session.id}/live-translation/jitsi?${eventQuery(session.eventId)}`, { method: "POST", body: JSON.stringify({ roomUrl: jitsiUrl.trim(), password: jitsiPassword }) });
      setJitsiPassword("");
      setData(current => current ? { ...current, jitsi: result.jitsi } : current);
      await refresh();
    } catch (error) { setLocalError(error instanceof Error ? error.message : "No se pudo conectar a Jitsi"); }
    finally { setBusy(false); }
  }

  async function stopJitsi() {
    setBusy(true); setLocalError("");
    try { await api(`/api/sessions/${session.id}/live-translation/jitsi?${eventQuery(session.eventId)}`, { method: "DELETE" }); await refresh(); }
    catch (error) { setLocalError(error instanceof Error ? error.message : "No se pudo detener Jitsi"); }
    finally { setBusy(false); }
  }

  function stop() {
    stopMediaRef.current();
    if (socketRef.current?.readyState === WebSocket.OPEN) socketRef.current.send(JSON.stringify({ type: "stop" }));
    setData((current) => current ? { ...current, status: "processing" } : current);
  }

  const jitsiActive = ["connecting", "waiting", "live"].includes(data?.jitsi?.state || "");
  const active = data?.status === "live" || jitsiActive;
  const aiAvailable = Boolean(data?.ai?.available);
  const selectedDevice = audioDevices.find((device) => device.deviceId === selectedAudioDeviceId);
  const testAudioName = localTestAudio?.name || data?.testAudio?.name || "";
  return <section className="live-translation-capture field-wide">
    <header><div><span className="eyebrow">Traducción simultánea por IA</span><h3>Captura y transmisión de la sesión</h3><p>El audio original se conserva. Cada oyente recibe voz y subtítulos en el idioma que seleccione.</p></div><span className={`live-translation-status ${data?.status || "idle"}`}>{!data ? "Comprobando…" : !aiAvailable ? "IA no disponible" : data.jitsi?.state === "connecting" ? "Conectando a Jitsi" : data.jitsi?.state === "waiting" ? "Esperando audio" : data.status === "live" ? "● En vivo" : data.status === "processing" ? "Procesando" : data.status === "ready" ? "Grabación disponible" : "Preparado"}</span></header>
    {(loadError || localError || data?.errorMessage || (!aiAvailable && data?.ai?.reason)) && <div className="inline-error">{loadError || localError || data?.errorMessage || data?.ai?.reason}</div>}
    {data?.jitsi && <div className={data.jitsi.state === "error" ? "inline-error" : "capture-jitsi-status"}><strong>Jitsi · servidor</strong><p>{data.jitsi.message}</p>{jitsiActive && <small>{data.jitsi.audioDetected ? "Audio detectado" : "Sin voz detectada todavía"} · {Math.round(data.jitsi.receivedBytes / 1024)} KB recibidos. Podés cerrar este panel: la captura continúa en el servidor.</small>}</div>}
    {!active && data?.status !== "processing" && <div className="capture-source-grid">
      <section className="capture-source-card"><span className="eyebrow">Archivo de audio</span><h4>Subir una grabación</h4><p>Elegí un MP3, WAV, M4A o WebM de esta computadora. Se usa para esta prueba y no modifica el archivo guardado en el evento.</p><label className="action-icon-button secondary capture-file-button" title="Seleccionar archivo" aria-label="Seleccionar archivo"><ActionIcon name="upload" /><span className="visually-hidden">Seleccionar archivo</span><input type="file" accept="audio/*,.mp3,.wav,.m4a,.webm" onChange={(event) => setLocalTestAudio(event.target.files?.[0] || null)} /></label>{testAudioName && <small className="capture-selected-source">Seleccionado: {testAudioName}</small>}<IconActionButton label={testAudioName ? `Probar con ${testAudioName}` : "Probar archivo de audio"} icon="play" tone="primary" busy={busy} disabled={busy || !aiAvailable || !testAudioName} onClick={startTestAudio} /></section>
      <section className="capture-source-card"><span className="eyebrow">Entrada en vivo</span><h4>Micrófono, línea o USB</h4><p>El navegador muestra las entradas que reconoce el sistema operativo. Conectá primero la consola o el dispositivo USB.</p><label className="field"><span>Dispositivo de entrada</span><select value={selectedAudioDeviceId} onChange={(event) => setSelectedAudioDeviceId(event.target.value)}><option value="">Entrada predeterminada del sistema</option>{audioDevices.map((device, index) => <option value={device.deviceId} key={device.deviceId || index}>{device.label || `Entrada de audio ${index + 1}`}</option>)}</select></label><IconActionButton label="Detectar entradas de audio" icon="search" busy={detectingDevices} disabled={detectingDevices || active} onClick={detectAudioInputs} /><IconActionButton label={`Iniciar desde ${selectedDevice?.label || "entrada de audio"}`} icon="play" tone="primary" busy={busy} disabled={busy || !aiAvailable} onClick={startMicrophone} /></section>
      <section className="capture-source-card capture-jitsi-card"><span className="eyebrow">Jitsi desde el servidor · Prueba</span><h4>{data?.videoMeetingUrl ? "Sala Video Meeting de la sesión" : "Conectar una sala"}</h4><p>El participante “Organize · Traducción” entra con cámara y micrófono apagados. No requiere instalar nada en tu computadora.</p><label className="field"><span>Enlace de la sala</span><input type="url" readOnly={Boolean(data?.videoMeetingUrl)} value={jitsiUrl} placeholder={`${data?.jitsiBaseUrl || "https://meet.virtualmedica.com"}/NombreDeLaSala`} onChange={event => setJitsiUrl(event.target.value)} /></label>{data?.videoMeetingUrl && <small>Sala asignada automáticamente a las participaciones Video Meeting. La traducción comienza sólo al pulsar Conectar.</small>}<label className="field"><span>Contraseña de la sala (si corresponde)</span><input type="password" autoComplete="off" value={jitsiPassword} onChange={event => setJitsiPassword(event.target.value)} /></label><small>Si hay sala de espera, el moderador debe admitir al participante. El audio recibido se grabará y se enviará a traducción.</small><IconActionButton label="Conectar audio de Jitsi desde el servidor" icon="play" tone="primary" busy={busy} disabled={busy || !aiAvailable || !jitsiUrl.trim()} onClick={startJitsi} /></section>
    </div>}
    <div className="live-translation-actions">
      {active && <IconActionButton label={testCountdown ? `Cancelar prueba (${testCountdown})` : "Finalizar y generar resumen"} icon="stop" tone="danger" busy={busy} disabled={busy} onClick={jitsiActive ? stopJitsi : stop} />}
      <IconActionLink label="Abrir escucha móvil" icon="external" href={`/escuchar/${session.id}`} target="_blank" rel="noreferrer" />
    </div>
    {testCountdown > 0 && <div className="inline-success">La transmisión comienza en {testCountdown} segundos. Abrí esta sesión en la app, tocá “Traducción simultánea” y activá el audio.</div>}
    {data?.sourceTranscript && <details><summary>Transcripción original en curso</summary><p>{data.sourceTranscript}</p></details>}
    {data?.status === "ready" && <div className="live-translation-archive">{data.recordingAvailable && data.recordingUrl ? <audio controls preload="metadata" src={data.recordingUrl} /> : <div className="inline-warning">Esta sesión finalizó sin audio grabado. Podés iniciar una nueva captura desde este panel.</div>}<p>{data.summary}</p></div>}
  </section>;
}
